import { create } from 'zustand';

export type EmbedSource = 'optisigns' | null;

/** Industry classification of the embed user's company, sourced from scio3's ON_INIT response. */
export interface Industry {
  primary: string | null;
  secondary: string | null;
  reason: string | null;
}

/** Onboarding survey signal for the embed user, sourced from scio3's ON_INIT response. */
export interface Onboarding {
  audience: 'internal' | 'external' | 'both' | null;
  useCases: string[];
  useCasesOther: string | null;
}

interface EmbedState {
  isEmbedded: boolean;
  embedSource: EmbedSource;
  workspaceId: string | null;
  parentOrigin: string | null;
  /** Firebase ID token from parent (scio3), used for API auth in embed mode */
  parentToken: string | null;
  /** OptiSigns account context from the ON_INIT handshake — attached to embed lead tracking. */
  scioTeamId: string | null;
  scioTeamName: string | null;
  scioRole: string | null;
  /**
   * True when an OptiSigns embed user doesn't have sufficient role to access OptiDev.
   * They were not pre-created by the webhook (role below sync threshold).
   * UI should show "Contact your admin" instead of the app.
   */
  needsAdminAccess: boolean;
  /**
   * True when user is authenticated in embed mode but no OptiDev workspace exists yet.
   * Detected via empty workspaceId in ON_INIT bridge response.
   * UI shows EmbedOnboarding; cleared once PROVISION_WORKSPACE succeeds.
   */
  needsProvisioning: boolean;
  /** Industry (primary/secondary/reason) for the embed user's company. Null if not synced. */
  industry: Industry | null;
  /** Onboarding survey signal (audience/useCases/useCasesOther) for the embed user. Null if not synced. */
  onboarding: Onboarding | null;
  setEmbedMode: (source: EmbedSource, workspaceId: string | null) => void;
  setParentOrigin: (origin: string) => void;
  setParentToken: (token: string) => void;
  setScioContext: (ctx: { teamId: string | null; teamName: string | null; role: string | null }) => void;
  setNeedsAdminAccess: (value: boolean) => void;
  setNeedsProvisioning: (value: boolean) => void;
  setIndustry: (industry: Industry | null) => void;
  setOnboarding: (onboarding: Onboarding | null) => void;
  clearEmbedMode: () => void;
}

/**
 * Detect embed params from URL on initial load.
 * ?embed=optisigns&ws=xxx
 */
function detectEmbed(): { source: EmbedSource; workspaceId: string | null } {
  if (typeof window === 'undefined') return { source: null, workspaceId: null };
  const params = new URLSearchParams(window.location.search);
  const embed = params.get('embed');
  if (embed === 'optisigns') {
    return { source: 'optisigns', workspaceId: params.get('ws') };
  }
  return { source: null, workspaceId: null };
}

const initial = detectEmbed();

/**
 * Embed state store — session-only, NOT persisted to localStorage.
 * Tracks whether the app is running inside an iframe (e.g. scio3).
 */
export const useEmbedStore = create<EmbedState>((set) => ({
  isEmbedded: initial.source !== null,
  embedSource: initial.source,
  workspaceId: initial.workspaceId,
  parentOrigin: null,
  parentToken: null,
  scioTeamId: null,
  scioTeamName: null,
  scioRole: null,
  needsAdminAccess: false,
  needsProvisioning: false,
  industry: null,
  onboarding: null,
  setEmbedMode: (source, workspaceId) =>
    set({ isEmbedded: source !== null, embedSource: source, workspaceId }),
  setParentOrigin: (origin) => set({ parentOrigin: origin }),
  setParentToken: (token) => set({ parentToken: token }),
  setScioContext: (ctx) =>
    set({ scioTeamId: ctx.teamId, scioTeamName: ctx.teamName, scioRole: ctx.role }),
  setNeedsAdminAccess: (value) => set({ needsAdminAccess: value }),
  setNeedsProvisioning: (value) => set({ needsProvisioning: value }),
  setIndustry: (industry) => set({ industry }),
  setOnboarding: (onboarding) => set({ onboarding }),
  clearEmbedMode: () =>
    set({ isEmbedded: false, embedSource: null, workspaceId: null, parentOrigin: null, parentToken: null, scioTeamId: null, scioTeamName: null, scioRole: null, needsAdminAccess: false, needsProvisioning: false, industry: null, onboarding: null }),
}));
